/**
 * Leaderboard rank derivation — the one place that decides what "#N" means
 * for the board currently on screen. Pure, so it can be unit tested without
 * React or a DOM.
 *
 * Two kinds of board come back from `GET /gamification/leaderboard`:
 *
 *   MULTI-ROW — the viewer can see other members (a company-wide or
 *     department-scoped Work Role, or an admin). Rank is simply the 1-based
 *     position in the points-sorted rows, so a department-filtered view ranks
 *     identically to what a member of that department sees.
 *
 *   SELF-ONLY — the viewer's scope collapsed to just themselves: their Work
 *     Role carries no `leaderboard.*` operation, or they have no workspace /
 *     department. The backend then also sends `viewerRank` / `viewerPlayers`,
 *     the viewer's TRUE standing measured against the peers they are actually
 *     ranked among (same rank or more junior, in their department).
 *
 * The bug this exists to prevent: positional rank was applied to BOTH kinds.
 * On a self-only board the viewer is the only row, so `i + 1` always produced
 * #1 — the page crowned a member "#1 · CHAMPION" while the My Rank card right
 * above it correctly read #2, and showed a member with ZERO points as the
 * champion of a 3-player peer group.
 */

/**
 * True when the response describes a self-only board.
 *
 * Keyed on `viewerPlayers`, NOT `viewerRank`: the backend sets both only for
 * a self-only board, but `viewerRank` is legitimately null there when the
 * viewer has scored nothing in the window, so it cannot distinguish
 * "self-only and unranked" from "ordinary board".
 *
 * @param {{viewerPlayers?: number|null}|null|undefined} payload
 */
export function isSelfOnlyBoard(payload) {
  return (payload?.viewerPlayers ?? null) !== null
}

/**
 * Rank to display for the row at `index` of the displayed board.
 *
 * @param {number} index 0-based position in the points-sorted rows
 * @param {{selfOnly?: boolean, viewerRank?: number|null}} [opts]
 * @returns {number|null} null = genuinely unranked (render "—")
 */
export function rankForRow(index, { selfOnly = false, viewerRank = null } = {}) {
  if (selfOnly) return viewerRank ?? null
  return index + 1
}

/**
 * The viewer's own rank for the "My Rank" card.
 *
 * `serverRank` (the true standing) always wins. Otherwise fall back to the
 * viewer's position in the visible rows — but NEVER on a self-only board,
 * where that position is always #1 and would contradict the board.
 *
 * @param {{serverRank?: number|null, selfOnly?: boolean, myRowRank?: number|null}} [opts]
 * @returns {number|null} null = unranked (render "—")
 */
export function myRankFor({ serverRank = null, selfOnly = false, myRowRank = null } = {}) {
  if (serverRank != null) return serverRank
  if (selfOnly) return null
  return myRowRank ?? null
}
